import React from 'react';
import { getFullMoveCount, sideHasOnlyKing } from '../engine/enginePolicy';

export function OpponentPreviewCard({
  theme,
  roundNumber,
  phase,
  game,
  blackBudget,
  whiteValue,
  startingWhiteValue,
  showStartGlow,
  startGlowAllowed,
  showEnemyPreview,
  enemyPreviewGlowActive,
  showResultOverlay,
  oppBtnHover,
  oppBtnActive,
  onStartBattle,
  onPresentRoundResult,
  onShowPreview,
  onClearPreview,
  onSetOppBtnHover,
  onSetOppBtnActive
}) {
  const cardColor = theme === 'dark' ? '#f1f1f1' : '#111';
  const roundNum = roundNumber || 1;

  return (
    <div style={{ width: 280 }}>
      <div style={{ background: 'transparent', color: cardColor, border: '1px solid #555', borderRadius: 8, padding: 10, paddingLeft: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10, minHeight: 28 }}>
          <div style={{ flex: 1, textAlign: 'left', marginTop: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 15 }}>{`Round ${roundNum} - ${phase === 'prep' ? 'Setup' : 'Battle'}`}</span>
          </div>
        </div>
        {phase === 'prep' ? (
          <>
            <div style={{ fontSize: 14, marginTop: -2, marginBottom: 10 }}>Prepare your board!</div>
            <div style={{ marginBottom: 12 }}>
              <button 
                onClick={onStartBattle} 
                className={showStartGlow && startGlowAllowed ? 'gc-glow-blue' : ''} 
                style={{ padding: '10px 18px', fontSize: 17, cursor: 'pointer' }}
              >
                Start Battle
              </button>
            </div>
          </>
        ) : (
          <>
            {(() => {
              const moves = getFullMoveCount(game);
              const canSkip = (() => { 
                try { 
                  const b = sideHasOnlyKing(game, 'b'); 
                  return !showResultOverlay && b; 
                } catch (_) { return false; } 
              })();
              const skipStyle = { padding: '10px 18px', fontSize: 17, cursor: 'pointer' };
              return (
                <>
                  <div style={{ fontSize: 14, marginTop: -2, marginBottom: 10 }}>{`Moves: ${moves}`}</div>
                  <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
                    {canSkip ? (
                      <button
                        onClick={() => {
                          onPresentRoundResult({
                            winner: 'White',
                            reason: 'black has no pieces',
                            resultType: 'win'
                          });
                        }}
                        title="End now (win)"
                        style={skipStyle}
                      >
                        Skip
                      </button>
                    ) : null}
                  </div>
                </>
              );
            })()}
          </>
        )}
        {roundNum > 1 ? (
          <>
            <div style={{ borderTop: '1px solid #555', margin: '10px 0' }} />
            {(() => {
              const isSetup = phase === 'prep';
              const leftValue = isSetup ? whiteValue : (startingWhiteValue != null ? startingWhiteValue : whiteValue);
              const deemph = theme === 'dark' ? '#ddd' : '#444';
              const diff = (leftValue || 0) - (blackBudget || 0);
              let playerColor = theme === 'dark' ? '#fff' : '#111';
              // Only show comparison colors during prep phase
              if (isSetup) {
                if (diff <= -3) {
                  playerColor = '#c62828';
                } else if (diff <= -1) {
                  playerColor = '#f9a825';
                } else if (diff > 4) {
                  playerColor = '#2e7d32';
                }
              }
              const playerValueColor = playerColor;
              const playerLabel = isSetup ? 'Your board value:' : 'Your starting board value:';
              const enemyLabel = isSetup ? 'Enemy board value:' : 'Enemy starting board value:';
              return (
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-start', gap: 16, marginBottom: 12 }}>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: 12, color: deemph, marginBottom: 2 }}>{playerLabel}</div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: playerValueColor }}>{`$${leftValue}`}</div>
                  </div>
                  <div style={{ fontSize: 24, lineHeight: '28px', fontWeight: 800, color: deemph }}>/</div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: 12, color: deemph, marginBottom: 2 }}>{enemyLabel}</div>
                    <div style={{ fontSize: 28, fontWeight: 800 }}>{`$${blackBudget}`}</div>
                  </div>
                </div>
              );
            })()}
          </>
        ) : null}
        {roundNum > 1 && phase === 'prep' ? (
          <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 8 }}>
            <button
              className={enemyPreviewGlowActive ? 'gc-glow-blue' : ''}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onShowPreview();
              }}
              onMouseEnter={() => onSetOppBtnHover(true)}
              onMouseLeave={() => { onSetOppBtnHover(false); onSetOppBtnActive(false); }}
              onMouseDown={() => onSetOppBtnActive(true)}
              onMouseUp={() => onSetOppBtnActive(false)}
              style={{
                cursor: 'pointer', padding: '8px 14px', fontSize: 15, borderRadius: 6,
                border: '1px solid #555',
                background: oppBtnActive ? (theme === 'dark' ? '#1c1c1c' : '#e6e6e6') : (oppBtnHover ? (theme === 'dark' ? '#242424' : '#f0f0f0') : 'transparent'),
                color: cardColor,
                transition: 'background 120ms ease'
              }}
            >
              <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, marginRight: 6, color: 'inherit' }}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              </span>
              Example enemy
            </button>
            {showEnemyPreview ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onClearPreview();
                }}
                style={{
                  cursor: 'pointer', padding: '8px 14px', fontSize: 15, borderRadius: 6,
                  border: '1px solid #555',
                  background: theme === 'dark' ? '#242424' : '#f0f0f0',
                  color: cardColor
                }}
              >
                Clear
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

